import { chunk } from 'lodash';
/* eslint-disable max-len */
/* eslint-disable no-useless-catch */
import pMap from 'p-map';
import uuidv4 from 'uuid/v4';
import { settings } from '../../config/settings';
import {
  contentApi,
  endpoints,
  getApiFromUrl,
  mailerApi,
  namedSetsApi,
  newsletterApi,
  personsApi,
  privateStateApi,
  proposalApi,
  trainingApi,
} from '../api/apiConfig';
import * as newsletterTypes from '../constants/newsletterTypes';
import { findExternalRelationType, getResourceKey } from '../helpers/documentHelpers';
import { sendNewsletterSettingsBatchCmd } from './documentListCommands';
import { sendWebsitesBatchCmd } from './websitesCommands';

const commonUtils = require('@kathondvla/sri-client/common-utils');

export const openWindowCmd = async (window, url, name) => {
  return window.open(url, name);
};

/**
 * Load an array of hrefs from the general api base url. They could belong to different api's
 */
export const loadHrefsCmd = async (hrefs, key) => {
  let results = [];
  if (hrefs) {
    const baseHrefs = new Set(hrefs.map((h) => commonUtils.getPathFromPermalink(h)));
    const data = await Promise.all(
      [...baseHrefs].map((href) => {
        // special case: if base is /content we do batch because possibly we need to load a lot of resources (url might be too long)
        // note: this can be extended to other bases but not all (eg. /vakken, /persons don't support batch)
        const endpoint = endpoints.get(href) || {
          client: getApiFromUrl(href),
          supportsBatch: false,
        };
        const batchHref = endpoint.supportsBatch ? `${href}/batch` : null;
        return endpoint.client.getAllHrefs(
          hrefs.filter((h) => h.startsWith(href)),
          {},
          { inBatch: batchHref, caching: { timeout: 3000 } },
        );
      }),
    );
    results = data.reduce((accumulator, result) => {
      result = result.map((r) => r.$$expanded || r);
      return [...accumulator, ...result];
    }, []);
  }
  return { results, key, hrefs };
};

export const loadDocumentCmd = async (key) => {
  const contents = await contentApi.getAll('/content', { root: `/content/${key}`, limit: 6000 });
  const externalResources = await loadExternalResources(contents);
  return [...contents, ...externalResources];
};

const loadExternalResources = async (contents) => {
  // expand those external resources that are part of the document tree aswell
  // (eg. /sam subjects, domain, etc)
  const externalResourcesHrefs = contents.reduce((list, content) => {
    const hrefs = content.$$relationsTo.reduce((extHrefs, relation) => {
      relation = relation.$$expanded;
      if (relation.from.href.startsWith('/') && !relation.from.href.startsWith('/content')) {
        extHrefs.push(relation.from.href);
      }
      return extHrefs;
    }, []);
    list = [...list, ...hrefs];
    return list;
  }, []);
  const externalResources = await loadHrefsCmd(externalResourcesHrefs);
  return externalResources.results.map((r) => {
    // fill with default fields of a content-api resource alike node
    r.$$relationsFrom = [];
    r.$$relationsTo = [];
    r.attachments = [];
    if (r.$$meta.type === 'VAK') {
      // special case for /vakken external resources
      r.$$meta.type = 'COMMONS_SUBJECT';
      r.label = '(oude structuur)';
    }
    r.type = r.type?.href ? findExternalRelationType(r.type.href) : r.$$meta.type;
    return r;
  });
};

const loadReferences = async (contents) => {
  const referenceHrefs = contents.reduce((resourceHrefs, content) => {
    const hrefs = content.$$relationsTo.reduce((hrefs, relation) => {
      hrefs.push(relation.$$expanded.from.href);
      return hrefs;
    }, []);
    return [...resourceHrefs, ...hrefs];
  }, []);
  const resources = await loadHrefsCmd(referenceHrefs);
  return resources.results;
};

async function getNamedSetChildren() {
  const namedSets = await namedSetsApi.getRaw(
    '/namedsets/query/subsets',
    { tags: 'mainstructure_outype' },
    { caching: { timeout: 43200 } },
  );

  const namedSetsMap = new Map();
  namedSets.results.forEach((item) => {
    if (item.subsets?.length) {
      namedSetsMap.set(
        item.namedset.href,
        item.subsets.map((s) => s.href),
      );
    }
  });

  return namedSetsMap;
}

const getInherited = (node, property) => {
  return node[property] ? node[property] : getInherited(node.$$parent, property);
};

const getMainstructuresOuTypeCombinations = (node, namedSetsMap) => {
  const mainstructuresOuTypeCombinations = getInherited(node, 'mainstructuresOuTypeCombinations');
  return mainstructuresOuTypeCombinations.reduce(
    (mainstructuresOuTypeCombinations, mainstructuresOuTypeCombination) => {
      return [
        ...mainstructuresOuTypeCombinations,
        mainstructuresOuTypeCombination,
        ...(namedSetsMap.get(mainstructuresOuTypeCombination) || []),
      ];
    },
    [],
  );
};

const getBatchParams = (node, themes, mainstructuresOuTypeCombinations) => {
  let themeHrefs;

  if (node.themes?.length) {
    themeHrefs = [
      ...node.themes.reduce((themesMap, theme) => {
        const recursiveChildren = themes.get(theme);
        if (recursiveChildren) {
          recursiveChildren.forEach((t) => themesMap.add(t.href));
        } else {
          console.warn(`Theme ${theme} not found in referenceframe`);
        }
        themesMap.add(theme); // add the theme itself too.

        return themesMap;
      }, new Set()),
    ];
  }

  const coverage = getInherited(node, 'coverage');
  const positions = getInherited(node, 'positions');

  const batchParams = {
    mainstructuresOuTypeCombinationsOverlaps: mainstructuresOuTypeCombinations
      ? mainstructuresOuTypeCombinations.join(',')
      : '',
    coverageOverlaps: coverage ? coverage.join(',') : '',
    positionsOverlaps: positions ? positions.join(',') : '',
  };

  if (themeHrefs) {
    batchParams.themesOverlaps = themeHrefs.join(',');
  } else {
    batchParams.themesIsNullOrEmpty = true;
  }

  return batchParams;
};

const sendEventsBatch = async (referenceGroups, themes, dateToSend, newsletterType) => {
  // at least one week in the future
  const startDateAfter = new Date(dateToSend);
  startDateAfter.setDate(startDateAfter.getDate() + 7);

  // no longer than 1 (general) or 3 (subject) months in the future
  const startDateBefore = new Date(dateToSend);
  const monthsInFuture = newsletterType.href === newsletterTypes.subjectSpecific.href ? 3 : 1;
  startDateBefore.setMonth(startDateBefore.getMonth() + monthsInFuture);

  const params = {
    type: 'TRAINING',
    status: 'CONFIRMED',
    startDateAfter: startDateAfter.toISOString(),
    startDateBefore: startDateBefore.toISOString(),
    'issued.startDateBefore': dateToSend,
    publicAndNotFull: true,
    firstOfSeries: true,
    limit: 5000,
  };

  const namedSetsMap = await getNamedSetChildren();

  const chunkSize = 30;
  const chunks = chunk(referenceGroups, chunkSize);

  const promises = chunks.map(async (chunk) => {
    const batch = trainingApi.createBatch();

    chunk.forEach((rg) => {
      const mainstructuresOuTypeCombinations = getMainstructuresOuTypeCombinations(
        rg,
        namedSetsMap,
      );
      const batchParams = {
        ...params,
        ...getBatchParams(rg, themes, mainstructuresOuTypeCombinations),
      };
      rg.$$batchGetUrl = commonUtils.parametersToString('/events', batchParams);
      batch.get(rg.$$batchGetUrl);
    });

    const result = await batch.send('/events/batch');
    return result;
  });

  const results = await Promise.all(promises);

  // Merge the results back together
  const mergedResults = [].concat(...results);
  return mergedResults;
};

export const loadEventsCmd = async (
  referenceGroups,
  themes,
  dateToSend,
  newsletterType,
  removedItems,
) => {
  const results = await sendEventsBatch(referenceGroups, themes, dateToSend, newsletterType);
  const removedItemSet = removedItems ? new Set(removedItems.map((e) => e.href)) : new Set();
  return {
    referenceGroups: results.map((r) => {
      return {
        ...referenceGroups.find((g) => g.$$batchGetUrl === r.href),
        $$events: r.body.results
          .map((e) => e.$$expanded)
          .filter(
            (e) =>
              !removedItemSet.has(e.$$meta.permalink) &&
              (!e.issued?.endDate || e.issued?.endDate >= dateToSend),
          ),
      };
    }, []),
  };
};

const sendTeaserBatch = async (
  sections,
  themes,
  dateToSend,
  newsletterType,
  currentDocumentHref,
) => {
  const twoMonthsBack = new Date(dateToSend);
  twoMonthsBack.setMonth(twoMonthsBack.getMonth() - 2);

  const params = {
    type: 'TEASER',
    issuedAfter: twoMonthsBack.toISOString(),
    issuedBefore: dateToSend,
    importanceIn: 'HIGH,MEDIUM',
    notIncludedInWithRootNewsletterType: newsletterType.href,
    excludeNewsletter: currentDocumentHref,
    limit: 6000,
  };

  // a teaser with a theme is added to all sections with that theme
  const sectionsWithTeaser = sections.filter((s) => s.themes?.length);
  // a teaser without a theme is only added to the first section without a theme
  const sectionsWithoutTheme = sections.filter((s) => !s.themes || !s.themes.length);
  if (sectionsWithoutTheme.length) {
    sectionsWithTeaser.push(
      sectionsWithoutTheme.reduce((previous, current) => {
        return previous.$$index < current.$$index ? previous : current;
      }),
    );
  }

  const batch = contentApi.createBatch();
  sectionsWithTeaser.forEach((section) => {
    const mainstructuresOuTypeCombinations = getInherited(
      section,
      'mainstructuresOuTypeCombinations',
    );
    const batchParams = {
      ...params,
      ...getBatchParams(section, themes, mainstructuresOuTypeCombinations),
    };
    // we save this into $$batchGetUrl as to know which batch result is for which section. (order is messed up)
    section.$$batchGetUrl = commonUtils.parametersToString('/content', batchParams);
    batch.get(section.$$batchGetUrl);
  });

  return batch.send('/content/batch');
};

export const loadTeasersCmd = async (
  sections,
  themes,
  dateToSend,
  newsletterType,
  removedItems,
  currentDocumentHref,
) => {
  const teasersPerSection = await sendTeaserBatch(
    sections,
    themes,
    dateToSend,
    newsletterType,
    currentDocumentHref,
  );
  const teasersWithSection = teasersPerSection.reduce((teasers, result) => {
    const section = sections.find((s) => s.$$batchGetUrl === result.href);
    delete section.$$batchGetUrl;
    return [
      ...teasers,
      ...result.body.results.map((r) => {
        return {
          ...r.$$expanded,
          $$section: section,
        };
      }),
    ];
  }, []);

  const removedItemSet = removedItems ? new Set(removedItems.map((e) => e.href)) : new Set();
  const teasers = teasersWithSection.filter((e) => !removedItemSet.has(e.$$meta.permalink)); // filter out the deleted teasers from settings.
  const references = await loadReferences(teasers);

  return { teasers, references };
};

export const sendBatchCmd = async (batch) => {
  if (!batch || !batch.length) {
    return [];
  }
  return contentApi.put('/content/batch', batch);
};

export const fetchDocumentPrivateStateCmd = async (documentKey, userKey) => {
  const privateStates = await privateStateApi.getAll('/privatestates', {
    owner: `/persons/${userKey}`,
    context: settings.privateState.component,
    'state.root': `/content/${documentKey}`,
  });
  return privateStates.length > 0
    ? privateStates[0]
    : {
        key: uuidv4(),
        isNew: true,
        context: settings.privateState.component,
        state: {
          root: `/content/${documentKey}`,
          collapsedNodes: [],
          lastRead: new Date().toISOString(),
        },
        owner: {
          href: `/persons/${userKey}`,
        },
      };
};

export const fetchDocumentProposalsCmd = async (key) => {
  return proposalApi.getAll('/proposals', {
    statusIn: ['IN_PROGRESS,SUBMITTED_FOR_REVIEW'].join(','),
    externalReferencesContains: `/content/${key}`,
  });
};

export const fetchProposalsCreatorsCmd = async (creatorHrefs) => {
  if (creatorHrefs && creatorHrefs.length > 0) {
    return personsApi.getAll('/persons', {
      hrefs: creatorHrefs,
    });
  }
};

// expand those external resources that are part of the document tree aswell but only in proposals
export const fetchProposalsExternalContentCmd = async (proposals) => {
  const externalResourcesHrefs = proposals.reduce((list, proposal) => {
    const hrefs = proposal.listOfRequestedChanges.reduce((extHrefs, change) => {
      const { resource } = change;
      if (resource && resource.relationtype === 'IS_INCLUDED_IN') {
        extHrefs.push(resource.from.href);
      }
      if (
        change.patch &&
        change.patch.filter((p) => p.path === '/relationtype' && p.value === 'IS_INCLUDED_IN')
          .length > 0
      ) {
        const fromPatch = change.patch.find((p) => p.path === '/from');
        if (fromPatch) {
          extHrefs.push(fromPatch.value.href);
        }
      }
      return extHrefs;
    }, []);
    list = [...list, ...hrefs];
    return list;
  }, []);
  const externalResources = await loadHrefsCmd(externalResourcesHrefs);
  return externalResources.results.map((r) => {
    // fill with default fields of a content-api resource alike node
    r.$$relationsFrom = [];
    r.$$relationsTo = [];
    r.type = !r.type || r.type.href ? r.$$meta.type : r.type;
    return r;
  });
};

/**
 * Upload file attachments as multipart
 * @param {*} config object
 * . baseUrl (optional) default /content
 */
export const putAttachmentsCmd = async (contentHref, attachments, config = {}) => {
  attachments = [].concat(attachments);

  const body = [];
  const fd = new FormData();

  await pMap(
    attachments,
    async (attachment) => {
      // set $$url for proposal uploads
      if (!attachment.$$url && attachment.$$base64) {
        attachment.$$url = attachment.$$base64;
      }

      if (config.external || !attachment.file) {
        // we need to first get the attachment from an external source
        let domain = '';
        if (attachment.$$url.startsWith('/content')) {
          domain = settings.apisAndUrls.contentApi;
        } else if (attachment.$$url.startsWith('/proposals')) {
          domain = settings.apisAndUrls.proposalApi;
        }

        const url = domain + attachment.$$url;
        const attachmentFetch = await fetch(url);
        const attachmentBlob = await attachmentFetch.blob();
        fd.append('data', attachmentBlob, attachment.name);

        attachment.file = { name: attachment.name };
      } else if (attachment.file) {
        fd.append('data', attachment.file, attachment.file.name);
      }

      const attachmentField = {
        key: attachment.key,
        type: attachment.type,
        name: attachment.file.name,
        width: attachment.width,
        height: attachment.height,
        resized: attachment.resized,
      };

      if (attachment.description) {
        attachmentField.description = attachment.description;
      }
      if (attachment.alt) {
        attachmentField.alt = attachment.alt;
      }
      if (attachment.rightsHolder) {
        attachmentField.rightsHolder = JSON.stringify(attachment.rightsHolder);
      }

      if (attachment.size) {
        attachmentField.size = attachment.size;
      }

      body.push({
        file: attachment.file.name,
        attachment: attachmentField,
        resource: { href: contentHref },
      });
    },
    { concurrency: 5 },
  );

  fd.append('body', JSON.stringify(body));

  const response = await fetch(
    `${config.baseUrl ?? `${settings.apisAndUrls.contentApi}/content`}/attachments`,
    {
      method: 'POST',
      body: fd,
    },
  );
  const responseBody = await response.json();
  console.log('fetch done', responseBody);
  return responseBody;
};

const uploadFiles = async (fileUploads) => {
  const hrefs = new Set(fileUploads.map((f) => f.body.relatedTo.href));
  await pMap(
    hrefs,
    async (href) => {
      await putAttachmentsCmd(
        href,
        fileUploads.filter((f) => f.body.relatedTo.href === href).map((f) => f.body),
      );
    },
    { concurrency: 5 },
  );
};

/**
 * Send a batch to the proposal-api
 * In case the contentBatch is filled it means it's applying a proposal that should
 * be saved in content-api
 * @param {*} batch
 * @param {*} contentBatch
 */
export const sendProposalsBatchCmd = async (
  batch,
  contentBatch,
  webpagesBatch,
  proposedFileUploads,
  fileUploads,
) => {
  if (contentBatch && contentBatch.length > 0) {
    await contentApi.put('/content/batch', contentBatch);
  }

  if (webpagesBatch && webpagesBatch.length > 0) {
    await sendWebsitesBatchCmd(webpagesBatch);
  }

  await proposalApi.put('/proposals/batch', batch);

  if (proposedFileUploads?.length > 0) {
    // upload files to proposal-api
    await pMap(
      proposedFileUploads,
      async (u) => {
        await putAttachmentsCmd(u.proposalHref, [u.attachment], {
          baseUrl: `${settings.apisAndUrls.proposalApi}/proposals`,
        });
      },
      { concurrency: 5 },
    );
  } else if (fileUploads?.length > 0) {
    // upload files directly to content-api
    await uploadFiles(fileUploads);
  }
};

export const saveDocumentCmd = async (
  contentBatch,
  webpagesBatch,
  newsletterSettingsBatch,
  fileUploads,
) => {
  if (contentBatch && contentBatch.length > 0) {
    await sendBatchCmd(contentBatch);
  }

  if (webpagesBatch && webpagesBatch.length > 0) {
    await sendWebsitesBatchCmd(webpagesBatch);
  }

  if (newsletterSettingsBatch && newsletterSettingsBatch.length > 0) {
    await sendNewsletterSettingsBatchCmd(newsletterSettingsBatch);
  }

  await uploadFiles(fileUploads);
};

export const loadNamedSetsCmd = async (tag) => {
  return await namedSetsApi.getAll('/namedsets', { tags: tag });
};

export const fetchTreeAsLeafCmd = async (resources, rootKey) => {
  const batch = [];
  resources.forEach((resource) => {
    batch.push({
      verb: 'GET',
      href: `/content?limit=500&leaf=${resource.body ? resource.body.key : resource.key}`,
    });
  });

  const results = await sendBatchCmd(batch);

  resources.forEach((resource) => {
    const batchResult = results.find(
      (r) => r.href.indexOf(resource.body ? resource.body.key : resource.key) !== -1,
    );
    resource.$$treeAsLeaf = batchResult.body.results.map((r) => r.$$expanded);
  });

  return rootKey ? { key: rootKey, results: resources } : resources;
};

export const fetchAllCmd = async (params, options = {}) => {
  let results = await contentApi.getAll('/content', params, { caching: { timeout: 5000 } });

  results.options = options;

  // need data to generate complete identifiers for each result
  if (options.fetchTreeAsLeaf) {
    results = await fetchTreeAsLeafCmd(results);
  }

  return results;
};

// load all content nodes + relations + active proposals for the given document
export const fetchExternalDocumentCmd = async (documentKey) => {
  const caching = { caching: { timeout: 5000 } };
  const [content, proposals] = await Promise.all([
    contentApi.getAll(
      '/content',
      {
        root: documentKey,
        $$expandPathToRoot: true,
      },
      caching,
    ),
    proposalApi.getAll(
      '/proposals',
      {
        statusIn: 'IN_PROGRESS,SUBMITTED_FOR_REVIEW',
        externalReferencesContains: `/content/${documentKey}`,
      },
      caching,
    ),
  ]);

  return {
    content,
    proposals,
    documentKey,
  };
};

// nodesWithRequiresRelations param format: object { property=nodeHref, value=relations}
// export const fetchThemeReferenceFramesMapCmd = async (nodesWithRequiresRelations) => {
//   try {
//     const caching = { timeout: 5000 };

//     const [themeStructuredDocuments] = await Promise.all([
//       contentApi.getAll(
//         '/content',
//         {
//           typeIn: 'REFERENCE_FRAME',
//           expand: 'NONE',
//         },
//         caching
//       ),
//     ]);

//     let requiredThemeHrefs = Object.keys(nodesWithRequiresRelations).reduce((list, key) => {
//       const requires = nodesWithRequiresRelations[key]
//         .filter((rel) => rel.relationtype === 'REQUIRES')
//         .map((rel) => rel.from.href);
//       return list.concat(requires);
//     }, []);
//     requiredThemeHrefs = Array.from(new Set(requiredThemeHrefs));

//     const leafTrees = await fetchTreeAsLeafCmd(
//       requiredThemeHrefs.map((href) => ({ key: getResourceKey(href) }))
//     );

//     return Object.keys(nodesWithRequiresRelations).reduce((result, href) => {
//       const referenceFrameMap = new Map();
//       themeStructuredDocuments.forEach((themeStructuredDocument) => {
//         referenceFrameMap.set(themeStructuredDocument.href, []);
//       });

//       nodesWithRequiresRelations[href].forEach((relation) => {
//         const themeResult = leafTrees.find((tr) => tr.key === getResourceKey(relation.from.href));
//         if (themeResult) {
//           const referenceFrame = themeResult.$$treeAsLeaf.find((n) => n.type === 'REFERENCE_FRAME');
//           if (referenceFrame) {
//             const referenceFrameHref = referenceFrame.$$meta.permalink;
//             const theme = themeResult.$$treeAsLeaf.find(
//               (n) => n.key === themeResult.key && n.type === 'THEME'
//             );
//             if (theme && referenceFrameMap.has(referenceFrameHref)) {
//               const value = referenceFrameMap.get(referenceFrameHref);
//               value.push(theme);
//             }
//           }
//         }
//       });

//       result[href] = referenceFrameMap;
//       return result;
//     }, {});
//   } catch (e) {
//     throw e;
//   }
// };

export const fetchRelationsWithExpandedPartCmd = async (
  key,
  relations,
  partToExpand,
  fetchTreeAsLeaf = true,
) => {
  const filteredRelations = relations.filter(
    (rel) => commonUtils.getPathFromPermalink(rel[partToExpand].href) === '/content',
  );

  const batch = [];
  filteredRelations.forEach((rel) => {
    batch.push({
      verb: 'GET',
      href: rel[partToExpand].href,
    });
  });

  let results = await sendBatchCmd(batch);

  // need data to generate complete identifiers for each result
  if (fetchTreeAsLeaf) {
    // WARNING: The following method is using the previous results and
    // iterating them adding some new information it gets from the API,
    // so the previous call IS RELEVANT
    results = await fetchTreeAsLeafCmd(results);
  }

  return {
    key,
    relationsToExpand: partToExpand === 'from' ? 'to' : 'from',
    relations,
    results,
  };
};

export const loadPracticalExampleZillIllustrationsCmd = async (key) => {
  // const caching = { timeout: 5000 };

  // await fetchRelationsPartCmd(key, relations, 'from', false);

  const relations = await contentApi.getAll(
    '/content/relations',
    {
      to: `/content/${key}`,
      relationtype: 'REFERENCES',
    },
    {
      expand: ['from'],
    },
  );

  // TODO add to relations those only present in the paramenter relationsTo list

  await pMap(
    relations,
    async (illustrationToPracticalExampleRelation) => {
      const illustrationFromRelations = await contentApi.getAll(
        '/content/relations',
        {
          from: `/content/${illustrationToPracticalExampleRelation.from.$$expanded.key}`,
        },
        {
          expand: ['to'],
        },
      );

      // eslint-disable-next-line max-len
      illustrationToPracticalExampleRelation.from.$$expanded.$$relationsFrom =
        illustrationFromRelations.filter(
          (relation) => relation.to.$$expanded.type.indexOf('CURRICULUM_ZILL_') !== -1,
        );

      await fetchTreeAsLeafCmd(illustrationFromRelations.map((rel) => rel.to.$$expanded));
    },
    { concurrency: 5 },
  );

  return {
    key,
    relations,
  };
};

export const sendEmailCmd = async (emailjobs) => {
  return Promise.all(
    emailjobs.map((emailjob) => mailerApi.put(`/mailer/emailjobs/${emailjob.key}`, emailjob)),
  );
};

export const fetchNewsletterSettingsCmd = async (key) => {
  return await newsletterApi.getAll('/newsletter/settings', {
    'newsletter.href': `/content/${key}`,
  });
};

export const patchNewsletterSettingsApprovalDateCmd = async (newsletterSettings) => {
  return newsletterApi.patch(
    `/newsletter/settings/${newsletterSettings.key}`,
    [{ op: 'replace', path: '/approvalDate', value: newsletterSettings.approvalDate }],
    { strip$$Properties: false },
  );
};

export const themeReferencesWithRootsCmd = async (nodeHref) => {
  const batch = [];
  batch.push({
    verb: 'GET',
    href: `/content?limit=500&themesContains=${nodeHref}`,
  });
  batch.push({
    verb: 'GET',
    href: `/content/relations?limit=500&from=${nodeHref}&relationtype=REQUIRES`,
  });

  const results = await sendBatchCmd(batch);

  const referencesInThemesKeys = results[0].body.results.map((r) => r.$$expanded.key);
  const requiresReferencesKeys = results[1].body.results.map((r) =>
    getResourceKey(r.$$expanded.to.href),
  );

  const referencesKeys = [...referencesInThemesKeys, ...requiresReferencesKeys];
  const leafs = await fetchTreeAsLeafCmd([...referencesKeys].map((k) => ({ key: k })));

  const referencedNodeRoots = new Set(
    leafs.map((leaf) => {
      const tree = leaf.$$treeAsLeaf;
      const rootTitle = tree[0].title;
      // eslint-disable-next-line no-nested-ternary
      const referenceNodeTitle = tree.find((n) => n.key === leaf.key).title;
      return `. ${rootTitle}${rootTitle !== referenceNodeTitle ? ` > ${referenceNodeTitle}` : ''}`;
    }),
  );

  const referencesRootNodes = Array.from(
    new Set(
      leafs.map((leaf) => {
        const tree = leaf.$$treeAsLeaf;
        return tree[0];
      }),
    ),
  );

  return new Promise(async (resolve, reject) => {
    try {
      if (referencesKeys.length > 0) {
        // eslint-disable-next-line prefer-promise-reject-errors
        reject({
          code: 'edit.referenceFrame.error.deleteThemeWithReferences',
          params: {
            references: `<br>${[...referencedNodeRoots].join('<br>')}`,
            referencesRootNodes,
          },
        });
      } else {
        resolve();
      }
    } catch (e) {
      throw reject(e);
    }
  });
};

let updateVmTimer;
let forceHashUpdateToAll = false;
export const updateDocumentVmDebounced = (dispatch, payload) => {
  clearTimeout(updateVmTimer);
  if (payload?.forceHashUpdateToAll) {
    forceHashUpdateToAll = true;
  }
  updateVmTimer = setTimeout(() => {
    dispatch({
      type: 'UPDATE_DOCUMENT_VIEW_MODEL_DEBOUNCED',
      payload: { forceHashUpdateToAll },
    });
    forceHashUpdateToAll = false;
  }, 10);
};
